import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, RotateCcw } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/redux/Toast/actions';
import CelebrationButton from '../ui/CelebrationButton/CelebrationButton';

const BookingCancelRestoreModal = ({
    open,
    onClose,
    onDelete = () => { },
    title = 'Delete',
    description = 'Please provide a remark before deleting.',
    confirmLabel = 'Delete',
    cancelLabel = 'Cancel',
    initialRemark = '',
    variant = 'danger' // 'danger' (delete) or 'warning' (restore)
}) => {
    const dispatch = useDispatch();
    const { isAnimate } = useSelector((state) => state.toast);
    const [remark, setRemark] = useState(initialRemark);

    const theme = {
        danger: {
            header: 'from-red-600 to-rose-600',
            button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        },
        warning: {
            header: 'from-amber-500 to-orange-500',
            button: 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-400',
        }
    };

    const currentTheme = theme[variant] || theme.danger;

    useEffect(() => {
        if (!open) {
            setRemark(initialRemark || '');
        }
    }, [open, initialRemark]);

    if (!open) return null;

    const handleConfirm = () => {
        if (!remark?.trim()) {
            const message = variant === 'warning' ? 'Remark is required to restore.' : 'Remark is required to delete.';
            dispatch(showToast(message, 'error'));
            return;
        }

        onDelete(remark.trim());
    };

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                    >
                        {/* Modal Header */}
                        <div className={`bg-gradient-to-r ${currentTheme.header} px-6 py-5 flex items-center justify-between`}>
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-white/20 rounded-lg">
                                    <Trash2 className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-white">{title}</h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-4 bg-white dark:bg-slate-900">
                            <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>

                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Remark <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    rows={4}
                                    placeholder="Enter remark..."
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-rose-400"
                                />
                            </div>

                            <div className="mt-4 flex gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                                >
                                    {cancelLabel}
                                </button>

                                <CelebrationButton
                                    onClick={handleConfirm}
                                    label={confirmLabel}
                                    className={`px-4 py-2 ${currentTheme.button} text-white rounded-lg font-medium transition-all`}
                                    icon={variant === 'warning' ? <RotateCcw className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    isSuccess={isAnimate}
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default BookingCancelRestoreModal;
